/**
 * @file 浮层定位计算
 * @description 下拉 / 右键菜单 / Tooltip 相对触发器（或坐标点）的视口定位：
 * 上下翻转、maxHeight、视口边界夹紧。
 */

import type { CSSProperties } from 'react'

export type FloatingBox = {
  /** 固定定位 top；placement=bottom 时有值 */
  top?: number
  /** 固定定位 bottom；placement=top 时有值（底边贴触发器上方） */
  bottom?: number
  /** 固定定位 left */
  left: number
  /** 面板宽度 */
  width: number
  /** 可用最大高度 */
  maxHeight: number
  /** 实际朝向 */
  placement: 'bottom' | 'top'
}

/** 下拉定位选项 */
export type DropdownBoxOptions = {
  /** 是否与触发器同宽；默认 true。false 时按 contentWidth / minWidth 定宽 */
  matchTriggerWidth?: boolean
  /** 内容实测宽度（matchTriggerWidth=false 时生效） */
  contentWidth?: number
}

/**
 * @param trigger - 触发器 getBoundingClientRect()
 * @param gap - 与触发器间距
 * @param minWidth - 面板最小宽度；默认与触发器同宽
 * @param options - 宽度策略
 */
export function computeDropdownBox(
  trigger: DOMRect,
  gap = 4,
  minWidth?: number,
  options?: DropdownBoxOptions,
): FloatingBox {
  const pad = 8
  const vw = window.innerWidth
  const vh = window.innerHeight
  const spaceBelow = vh - trigger.bottom - gap - pad
  const spaceAbove = trigger.top - gap - pad
  const placement: 'bottom' | 'top' =
    spaceBelow >= 160 || spaceBelow >= spaceAbove ? 'bottom' : 'top'
  const maxHeight = Math.max(96, placement === 'bottom' ? spaceBelow : spaceAbove)

  const width =
    options?.matchTriggerWidth === false
      ? // 按内容 / 最小宽度定宽，但不得窄于触发器
        Math.max(trigger.width, options.contentWidth ?? 0, minWidth ?? 0)
      : Math.max(trigger.width, minWidth ?? trigger.width)
  let left = trigger.left
  if (left + width > vw - pad) left = Math.max(pad, vw - pad - width)
  if (left < pad) left = pad

  if (placement === 'bottom') {
    return {
      top: trigger.bottom + gap,
      left,
      width,
      maxHeight,
      placement,
    }
  }

  // 向上展开：用 bottom 锚定触发器顶边，避免按 maxHeight 推算 top 导致贴到视口顶部
  return {
    bottom: vh - trigger.top + gap,
    left,
    width,
    maxHeight,
    placement,
  }
}

/**
 * 右键菜单定位：以鼠标坐标为锚点，视口边界内翻转 / 夹紧。
 * @param x 鼠标 clientX
 * @param y 鼠标 clientY
 * @param minWidth 面板最小宽度
 */
export function computeContextMenuBox(
  x: number,
  y: number,
  minWidth = 160,
): FloatingBox {
  const pad = 8
  const vw = window.innerWidth
  const vh = window.innerHeight

  // 高度先按视口剩余空间估计，实际面板受 maxHeight 限制出现滚动
  const spaceBelow = vh - y - pad
  const placement: 'bottom' | 'top' =
    spaceBelow >= 160 || spaceBelow >= y ? 'bottom' : 'top'
  const maxHeight = Math.max(
    96,
    placement === 'bottom' ? spaceBelow : y - pad,
  )

  const left = Math.min(Math.max(pad, x), vw - pad - minWidth)

  if (placement === 'bottom') {
    return { top: y, left, width: minWidth, maxHeight, placement }
  }
  return { bottom: vh - y, left, width: minWidth, maxHeight, placement }
}

/**
 * 将 FloatingBox 转为 fixed 面板 style
 * @param box - computeDropdownBox / computeContextMenuBox 结果
 */
export function floatingPanelStyle(box: FloatingBox): CSSProperties {
  return {
    left: box.left,
    width: box.width,
    maxHeight: box.maxHeight,
    top: box.placement === 'bottom' ? box.top : 'auto',
    bottom: box.placement === 'top' ? box.bottom : 'auto',
  }
}

/** Tooltip 方位 */
export type TooltipPlacement =
  | 'auto'
  | 'top'
  | 'bottom'
  | 'top-left'
  | 'top-right'
  | 'bottom-left'
  | 'bottom-right'

export type TooltipBox = {
  top: number
  left: number
  placement: 'top' | 'bottom'
}

/** Tooltip 定位选项 */
export type TooltipBoxOptions = {
  /** 方位；默认 auto（优先上方、水平居中） */
  placement?: TooltipPlacement
}

/**
 * Tooltip 定位：auto 优先上方，空间不足则下方；水平居中并夹紧视口；
 * 角落方位按触发器对应边对齐。
 * @param trigger 触发器 getBoundingClientRect()
 * @param tooltip 浮层尺寸
 * @param options 定位选项
 */
export function computeTooltipBox(
  trigger: DOMRect,
  tooltip: { width: number; height: number },
  options: TooltipBoxOptions = {},
): TooltipBox {
  const gap = 6
  const pad = 8
  const vw = window.innerWidth
  const vh = window.innerHeight
  const { placement: wanted = 'auto' } = options

  const canTop = trigger.top - gap - pad >= tooltip.height
  const placement: 'top' | 'bottom' =
    wanted === 'auto' ? (canTop ? 'top' : 'bottom') : wanted.startsWith('top') ? 'top' : 'bottom'

  let left: number
  if (wanted.endsWith('-left')) {
    left = trigger.left
  } else if (wanted.endsWith('-right')) {
    left = trigger.right - tooltip.width
  } else {
    left = trigger.left + trigger.width / 2 - tooltip.width / 2
  }
  left = Math.min(Math.max(pad, left), vw - pad - tooltip.width)

  const top =
    placement === 'top'
      ? trigger.top - gap - tooltip.height
      : trigger.bottom + gap

  return {
    top: Math.min(Math.max(pad, top), vh - pad - tooltip.height),
    left,
    placement,
  }
}
